// rounds/configuration types — step 5, « Jalons » (Figma 02.9 · 335:27423).
import { z } from "zod";

/**
 * The three milestones the backend models (`RoundMilestone.Kind`), in the design's order.
 *
 * ⚠ THREE IS THE CEILING, not a starting set. `unique_round_milestone_kind` allows one row per kind
 * per round, so there is no "add a milestone" — the step is three fixed questions, which is exactly
 * how the design draws it.
 */
export const MILESTONE_KINDS = [
  "EXAM_START",
  "RESULTS_ANNOUNCEMENT",
  "PROGRAM_START",
] as const;

export type MilestoneKind = (typeof MILESTONE_KINDS)[number];

/** « Date précise » vs « Intervalle depuis la clôture » (`RoundMilestone.ScheduleType`). */
export const MILESTONE_SCHEDULE_TYPES = ["EXACT", "AFTER_CLOSING"] as const;

export type MilestoneScheduleType = (typeof MILESTONE_SCHEDULE_TYPES)[number];

export const MILESTONE_OFFSET_UNITS = ["DAYS", "WEEKS", "MONTHS"] as const;

export type MilestoneOffsetUnit = (typeof MILESTONE_OFFSET_UNITS)[number];

export const MILESTONE_LABEL_KEYS = {
  EXAM_START: "round-create.milestones.kind.exam-start",
  RESULTS_ANNOUNCEMENT: "round-create.milestones.kind.results-announcement",
  PROGRAM_START: "round-create.milestones.kind.program-start",
} as const satisfies Record<MilestoneKind, string>;

export const MILESTONE_HELP_KEYS = {
  EXAM_START: "round-create.milestones.kind.exam-start-help",
  RESULTS_ANNOUNCEMENT:
    "round-create.milestones.kind.results-announcement-help",
  PROGRAM_START: "round-create.milestones.kind.program-start-help",
} as const satisfies Record<MilestoneKind, string>;

export const OFFSET_UNIT_LABEL_KEYS = {
  DAYS: "round-create.milestones.unit.days",
  WEEKS: "round-create.milestones.unit.weeks",
  MONTHS: "round-create.milestones.unit.months",
} as const satisfies Record<MilestoneOffsetUnit, string>;

// ── The draft ────────────────────────────────────────────────────────────────────────────────

/**
 * One milestone as the administrator edits it.
 *
 * ⚠ BOTH BRANCHES ARE HELD AT ONCE, not narrowed to the active one. `exact_at` and the offset pair
 * coexist so switching « Date précise » → « Intervalle » → back does not discard what was typed;
 * the wire body is narrowed at send time instead (`toMilestoneBody`). A discriminated union would be
 * a tidier type and a worse form — every toggle would silently erase a date.
 *
 * ⚠ AND AN UNSET MILESTONE IS A REAL, VALID STATE. The design shows no on/off switch, so "no exam
 * date yet" has to be expressible some other way: an empty offset (or empty date) means the
 * milestone is simply not sent. `PUT /milestones/` replaces the whole set, so absent means absent —
 * and readiness only faults milestones that EXIST while `is_active` with no schedule value. That is
 * what lets this step be genuinely optional rather than three fields you must invent answers for.
 */
export const MilestoneDraftSchema = z.object({
  kind: z.enum(MILESTONE_KINDS),
  schedule_type: z.enum(MILESTONE_SCHEDULE_TYPES),
  /** `datetime-local` wall time, converted at the wire boundary like step 1's dates. */
  exact_at: z.string(),
  /** Empty string rather than null — it is an `<input>` value before it is a number. */
  offset_value: z.string(),
  offset_unit: z.enum(MILESTONE_OFFSET_UNITS),
});

export type MilestoneDraft = z.infer<typeof MilestoneDraftSchema>;

export const RoundMilestonesDraftSchema = z.array(MilestoneDraftSchema);

export type RoundMilestonesDraft = z.infer<typeof RoundMilestonesDraftSchema>;

/**
 * The three rows, all unset.
 *
 * `AFTER_CLOSING` is the default branch because the design says why: « Un intervalle suffit si la
 * date exacte n'est pas connue ». At the moment a round is being built the exact dates usually are
 * not known, and an interval is the answer that stays true when the closing date moves.
 *
 * `WEEKS` because it is the unit the design uses for two of the three rows, and because days are too
 * fine and months too coarse for the gap between closing and a decision.
 */
export function defaultMilestoneDrafts(): MilestoneDraft[] {
  return MILESTONE_KINDS.map((kind) => ({
    kind,
    schedule_type: "AFTER_CLOSING",
    exact_at: "",
    offset_value: "",
    offset_unit: "WEEKS",
  }));
}

/**
 * True when the row carries enough to be sent.
 *
 * The gate for whether a milestone exists at all — see the note on `MilestoneDraftSchema`. Mirrors
 * `MilestoneSerializer.validate`: EXACT needs `exact_at`, AFTER_CLOSING needs a positive
 * `offset_value` (the serializer sets `min_value=1`, so 0 is rejected, not merely empty).
 */
export function isMilestoneSet(draft: MilestoneDraft): boolean {
  if (draft.schedule_type === "EXACT") return draft.exact_at.trim() !== "";
  const value = Number(draft.offset_value);
  return Number.isInteger(value) && value >= 1;
}

// ── The computed date the design shows as « ≈ 14 mai 2027 » ──────────────────────────────────

/**
 * Days per offset unit — MIRRORING THE BACKEND EXACTLY, approximation included.
 *
 * ⚠ `MONTHS: 30` IS WRONG AS A CALENDAR MONTH AND RIGHT AS AN IMPLEMENTATION. `serialize_milestone`
 * computes `closing_at + offset_value * multiplier` with this same table, so a client-side estimate
 * built on real month arithmetic would disagree with the `effective_at` the server returns — and the
 * administrator would watch the date change on save for no visible reason. The design hedges it with
 * « ≈ », which is the honest way to show a number derived this way.
 */
const DAYS_PER_UNIT: Record<MilestoneOffsetUnit, number> = {
  DAYS: 1,
  WEEKS: 7,
  MONTHS: 30,
};

/**
 * When this milestone lands, or null when it cannot be worked out.
 *
 * Null covers the cases the badge simply must not render for: an unset row, and an interval with no
 * closing date to count from (step 1 not filled in yet). Guessing either would put a confident date
 * on screen that no one chose.
 */
export function effectiveMilestoneDate(
  draft: MilestoneDraft,
  closingAt: string
): Date | null {
  if (!isMilestoneSet(draft)) return null;

  if (draft.schedule_type === "EXACT") {
    const exact = new Date(draft.exact_at);
    return Number.isNaN(exact.getTime()) ? null : exact;
  }

  if (!closingAt.trim()) return null;
  const closing = new Date(closingAt);
  if (Number.isNaN(closing.getTime())) return null;

  const days = Number(draft.offset_value) * DAYS_PER_UNIT[draft.offset_unit];
  return new Date(closing.getTime() + days * 86_400_000);
}
